import type { MessageCard } from '~/types/web-chat'
import type { DeleteItemsResult } from '~/stores/documents'

/**
 * What accepting a storage/scope confirm card needs — injected so the flow can be tested without
 * mounting `ChatPanel.vue`.
 */
export interface ConfirmCardAcceptDeps {
  settleCard: (card: MessageCard, status: 'confirmed' | 'cancelled') => void
  resolveConfirmToolCall: (card: MessageCard, response: unknown) => Promise<void>
  deleteItems: (ids: string[]) => Promise<DeleteItemsResult>
  moveItems: (ids: string[], destination: string) => Promise<void>
  renameItem: (id: string, name: string) => Promise<void>
  /** Creates the folder `name` under `path` and returns the created row's id and name. */
  createFolder: (name: string, path: string) => Promise<{ id: string; name: string }>
  /** Links one document to a case — a `case_documents` row (`cases.addCaseDocument`). */
  linkToCase: (caseId: string, itemId: string) => Promise<void>
  clearScope: () => Promise<void>
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/** `deleteItems` throws with its partial `{ deletedIds, failedIds }` attached (stores/documents.ts). */
function partialDeleteResult(err: unknown): DeleteItemsResult | null {
  const result = (err as { result?: DeleteItemsResult } | null)?.result
  return result && Array.isArray(result.deletedIds) && Array.isArray(result.failedIds) ? result : null
}

/**
 * The user accepted a delete/move/rename/create-folder/file-to-case/clear-scope card: settle it, run the tool's real effect,
 * then resume the paused ADK session with what actually happened — not with a blanket ok.
 *
 * THE RESUME IS UNCONDITIONAL (MAR-957). The card is settled before the effect runs, so
 * `pendingCard()` stops finding it; if a failed effect threw past the resume, the paused tool call
 * was never answered and the conversation hung. A failure is an outcome like any other: the agent
 * is told `ok: false` and why, and narrates it — rather than the session waiting forever.
 *
 * The response names what the effect REPORTED, never what was requested: a delete says
 * `deleteItems`' own `deletedIds` (the fabricated-success pattern from #795). The same outcome is
 * written onto a delete/move/rename card as `request.outcome`, which is what the card renders from:
 * `status === 'confirmed'` is set before the effect runs and says only that the user said yes.
 *
 * `run_workflow` and `convert_document` own their own flows and never reach here; offer cards have no
 * accept path (they resumed when they rendered) and return without resuming.
 */
export async function acceptConfirmCard(card: MessageCard, deps: ConfirmCardAcceptDeps): Promise<void> {
  let response: unknown
  switch (card.toolId) {
    case 'delete_files': {
      deps.settleCard(card, 'confirmed')
      const ids = card.request.items.map(item => item.id)
      try {
        const result = ids.length > 0 ? await deps.deleteItems(ids) : { deletedIds: [], failedIds: [] }
        response = { ok: true, deleted: result.deletedIds }
        card.request.outcome = { ok: true, succeeded: result.deletedIds.length, failed: 0 }
      } catch (err) {
        const partial = partialDeleteResult(err)
        const deleted = partial?.deletedIds ?? []
        const failed = partial?.failedIds ?? ids
        response = { ok: false, error: errorMessage(err), deleted, failed }
        card.request.outcome = { ok: false, succeeded: deleted.length, failed: failed.length }
      }
      break
    }
    case 'move_files': {
      deps.settleCard(card, 'confirmed')
      const ids = card.request.items.map(item => item.id)
      try {
        if (ids.length > 0) await deps.moveItems(ids, card.request.destination)
        // `destination` is named so the agent narrates WHERE the files went — a folder path, which is
        // never a case (MAR-988: the reply said "moved to the case" about a folder named after it).
        response = { ok: true, moved: ids, destination: card.request.destination }
        card.request.outcome = { ok: true }
      } catch (err) {
        response = { ok: false, error: errorMessage(err), failed: ids, destination: card.request.destination }
        card.request.outcome = { ok: false }
      }
      break
    }
    case 'rename_files': {
      deps.settleCard(card, 'confirmed')
      const { fileId, newName } = card.request
      try {
        if (fileId && newName) await deps.renameItem(fileId, newName)
        response = { ok: true }
        card.request.outcome = { ok: true }
      } catch (err) {
        response = { ok: false, error: errorMessage(err) }
        card.request.outcome = { ok: false }
      }
      break
    }
    case 'create_folder': {
      deps.settleCard(card, 'confirmed')
      const { name, path } = card.request
      try {
        if (!name) throw new Error('No folder name was proposed.')
        const folder = await deps.createFolder(name, path)
        response = { ok: true, created: { id: folder.id, name: folder.name, path } }
        card.request.outcome = { ok: true }
      } catch (err) {
        response = { ok: false, error: errorMessage(err) }
        card.request.outcome = { ok: false }
      }
      break
    }
    case 'file_to_case': {
      // Filing LINKS each document to the case; the files do not move (MAR-988). Linked one at a
      // time so a failure names exactly which documents did and did not reach the case.
      deps.settleCard(card, 'confirmed')
      const { caseId, caseName } = card.request
      const linked: string[] = []
      const failed: string[] = []
      let lastError: unknown
      for (const item of card.request.items) {
        try {
          await deps.linkToCase(caseId, item.id)
          linked.push(item.id)
        } catch (err) {
          failed.push(item.id)
          lastError = err
        }
      }
      const kase = { id: caseId, name: caseName }
      response = failed.length === 0 && linked.length > 0
        ? { ok: true, case: kase, linked, filesMoved: false }
        : { ok: false, case: kase, linked, failed, error: lastError ? errorMessage(lastError) : 'No documents to add.' }
      card.request.outcome = { ok: failed.length === 0 && linked.length > 0, succeeded: linked.length, failed: failed.length }
      break
    }
    case 'clear_chat_scope': {
      deps.settleCard(card, 'confirmed')
      try {
        await deps.clearScope()
        // The agent retries on resume, now unscoped — so the response says the scope is GONE, not
        // merely that the user clicked yes.
        response = { ok: true, scope: 'global' }
      } catch (err) {
        response = { ok: false, error: errorMessage(err) }
      }
      break
    }
    default:
      return
  }
  await deps.resolveConfirmToolCall(card, response)
}
